import React, { Component } from 'react';
import { Engine, Scene, SceneLoader, ArcRotateCamera, HemisphericLight, Vector3, Color3, Color4, DracoCompression, CubeTexture, Texture, StandardMaterial, Mesh , AnimationGroup, DefaultRenderingPipeline, WebXRHitTest, MeshBuilder, Quaternion, PointerEventTypes, WebXRState, WebXRDomOverlay, WebXRDefaultExperience, TransformNode} from '@babylonjs/core';
import '@babylonjs/loaders'; // Import loaders for .glb files

interface Scene3D {
  canvasRef: any,
  globalEngine: Engine | null,
  globalScene: Scene | null,
  globalMesh: Mesh | null,
  globalCamera: ArcRotateCamera | null,
  animationIdle: AnimationGroup | null,
  animationTalk: AnimationGroup | null,

  xr: WebXRDefaultExperience | null,
  
  globalPerformanceControl: {
    scalingLevel: number
    framesCount: number
    avgFrameTime: number
    minScalingLevel: number
    maxScalingLevel: number
    targetFramerate: number
    frameSamples: number
  }
}

interface Props {
  sceneLoaded: (value: boolean) => void,
  isARSupported: (value: boolean) => void,
  isOnARMode: (value: boolean) => void,
  isSpeaking: boolean,
  switchAR: boolean,
}

interface State {
  sceneLoaded: boolean,
  inARMode: boolean,
  //isARSupported: boolean
}

class Scene3D extends Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      sceneLoaded: false,
      inARMode: false,
      //isARSupported: false,
    }
    this.canvasRef = React.createRef();
    this.globalEngine = null;
    this.globalScene = null;
    this.globalMesh = null;
    this.globalCamera = null;
    this.animationIdle = null;
    this.animationTalk = null;
    this.xr = null;
    this.globalPerformanceControl = {
      scalingLevel: 1,
      framesCount: 0,
      avgFrameTime: 0,
      minScalingLevel: 1.0,
      maxScalingLevel: 1.5,
      targetFramerate: 30,
      frameSamples: 60,
    }
  }

  componentDidMount(): void {
    if (this.canvasRef !== null) {
      //SETUP ENGINE AND SCENE
      this.globalEngine = new Engine(this.canvasRef.current, true);
      this.globalEngine.disableUniformBuffers = true;
      this.globalScene = new Scene(this.globalEngine);

      this.globalScene.clearColor = new Color4(33 / 255, 31 / 255, 81 / 255, 1);

      //SETUP CAMERA
      this.globalCamera = new ArcRotateCamera("camera", Math.PI / 2, Math.PI / 2, 3, new Vector3(0, 1, 0), this.globalScene);
      
      this.globalCamera.attachControl(this.canvasRef.current, true);
      this.globalCamera.wheelDeltaPercentage = 0.1
      this.globalCamera.minZ = 0.001;
      this.globalCamera.maxZ = 20
      this.globalCamera.lockedTarget = new Vector3(0, 1, 0)
      //this.globalCamera.position = new Vector3(0, 1, 1)
      this.globalCamera.lowerRadiusLimit = 1.0
      this.globalCamera.upperRadiusLimit = 5.0
      //this.globalCamera.lowerBetaLimit = 80 * Math.PI / 180
      //this.globalCamera.upperBetaLimit = 90 * Math.PI / 180

      var defaultPipeline = new DefaultRenderingPipeline("default", false, this.globalScene, [this.globalCamera]);
      defaultPipeline.imageProcessing.contrast = 1.5;
      defaultPipeline.samples = 2 //AA

      //SETUP LIGHTING
      var light = new HemisphericLight("light", new Vector3(1, 1, 0), this.globalScene);
      light.intensity = 1

      DracoCompression.Configuration.decoder = {
        wasmUrl: "./draco/draco_decoder.js",
        wasmBinaryUrl: "./draco/draco_decoder.wasm",
        fallbackUrl: "./draco/draco_wasm_wrapper.js",
      };

      this.EnableARMode(this.globalScene);
      this.LoadAllTexturesAndModels(this.globalScene);

      this.globalScene.onBeforeRenderObservable.add(() => {
        if (this.globalScene)
          this.frameUpdate(0.001 * this.globalScene.deltaTime)
      });

      window.addEventListener("resize", this.Resize);

      this.globalEngine.runRenderLoop(() => {
        this.globalScene?.render();
      });
    }
  }

  componentWillUnmount(): void {
    if (this.globalScene)
      this.globalScene.dispose()
    if (this.globalEngine)
      this.globalEngine.dispose()
    window.removeEventListener("resize", this.Resize);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.isSpeaking !== this.props.isSpeaking && this.props.isSpeaking === true) {
      this.MakeTalkAnimation()
    }
    if (prevProps.isSpeaking !== this.props.isSpeaking && this.props.isSpeaking === false) {
      this.MakeIdleAnimation()
    }
    if (prevProps.switchAR !== this.props.switchAR && this.props.switchAR === true && this.xr) {
      if(this.state.inARMode === false){
        this.xr.baseExperience.enterXRAsync("immersive-ar", "local-floor" /*, optionalRenderTarget */ );
      }else{
        this.xr.baseExperience.exitXRAsync();
      }
    }
  }

  frameUpdate = (deltaTime: number): void => {
    if (!this.globalEngine || !this.globalScene || !this.globalCamera || !this.globalPerformanceControl || this.state.sceneLoaded === false)
      return;

    //PERFORMANCE CONTROL
    this.globalPerformanceControl.avgFrameTime += deltaTime
    this.globalPerformanceControl.framesCount += 1
    if (this.globalPerformanceControl.framesCount > this.globalPerformanceControl.frameSamples - 1) {
      const avgFrameRate = this.globalPerformanceControl.framesCount / this.globalPerformanceControl.avgFrameTime;
      this.globalPerformanceControl.framesCount = 0
      this.globalPerformanceControl.avgFrameTime = 0

      const scaleChage = avgFrameRate > this.globalPerformanceControl.targetFramerate ? -0.1 : 0.1;
      this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.scalingLevel + scaleChage
      if (this.globalPerformanceControl.scalingLevel > this.globalPerformanceControl.maxScalingLevel)
        this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.maxScalingLevel
      if (this.globalPerformanceControl.scalingLevel < this.globalPerformanceControl.minScalingLevel)
        this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.minScalingLevel

      if (this.globalPerformanceControl.scalingLevel !== this.globalEngine.getHardwareScalingLevel()) {
        this.globalEngine.setHardwareScalingLevel(this.globalPerformanceControl.scalingLevel);
      }
    }

  }

  Resize = () => {
    this.globalEngine?.resize();
  }

  MakeIdleAnimation = () => {
    this.animationTalk?.stop();
    this.animationTalk?.start(false,1,0,0)
    //this.animationIdle?.start(true, 1, 0 , 91)
    //this.animationIdle?.start(true)
  }

  MakeTalkAnimation = () => {
    //this.animationIdle?.stop();
    //this.animationTalk?.start(true, 1, 100 , 334)
    //this.animationTalk?.start(true, 1, 200)
    this.animationTalk?.start(true)
  }

  LoadCubeMapAsync = async (texturesUrl: string, scene: Scene): Promise<CubeTexture> => {
    return new Promise((resolve, reject) => {
      const cubeTexture = new CubeTexture(texturesUrl, scene, null, true, null,
        () => { resolve(cubeTexture) },
        (message, exception) => reject(new Error("Error loadingcube texture: " + message))
      )
    })
  }

  LoadTextureAsync = async (textureUrl: string, scene: Scene, invertY: boolean): Promise<Texture> => {
    return new Promise((resolve, reject) => {
      const texture: Texture = new Texture(textureUrl, scene, true, invertY, Texture.BILINEAR_SAMPLINGMODE,
        () => resolve(texture),
        (message, exception) => reject(new Error("Error loading texture: " + message))
      );
    })
  }
  
  EnableARMode = async (scene: Scene): Promise<void> => {
    this.xr = await scene.createDefaultXRExperienceAsync({
      uiOptions: {
        sessionMode: "immersive-ar",
      },
      optionalFeatures: true,
      disableDefaultUI: true,
    });
    if (!this.xr.baseExperience) {
      this.props.isARSupported(false)
      // no xr support
    } else {
      this.props.isARSupported(true)
      // all good, ready to go
    }
    this.xr.teleportation.detach();
    //await this.xr.baseExperience.enterXRAsync("immersive-ar", "local-floor" /*, optionalRenderTarget */ );
    //await this.xr.baseExperience.exitXRAsync();


    console.log(document.querySelector('.sceneui-container'))
    const featuresManager = this.xr.baseExperience.featuresManager;

    const xrTest = featuresManager.enableFeature(WebXRHitTest, "latest") as WebXRHitTest;
    featuresManager.enableFeature(WebXRDomOverlay, "latest", { element: ".sceneui-container" }, undefined, false);//permite poner como overlay la interfaz html

    const marker = MeshBuilder.CreateTorus('marker', { diameter: 0.15, thickness: 0.05 });
    marker.isVisible = false;
    marker.rotationQuaternion = new Quaternion();

    const box = MeshBuilder.CreateBox("box", { size: 0.5})
    box.isVisible = false;

    var hitTest : any = null;
    xrTest.onHitTestResultObservable.add((results: any) => {
        if (results.length) {
            marker.isVisible = true;
            hitTest = results[0];
            hitTest.transformationMatrix.decompose(marker.scaling, marker.rotationQuaternion, marker.position);
        } else {
            marker.isVisible = false;
        }
    });

    this.xr.baseExperience.onStateChangedObservable.add((state: WebXRState) => {
      switch(state) {
          case WebXRState.ENTERING_XR:
              console.log('ENTERING_XR')
              if(this.globalMesh){
                this.globalMesh.isVisible = false
                this.globalMesh.setEnabled(false);
              }
              marker.isVisible = true;
              marker.setEnabled(true);
              // fm.enableFeature(BABYLON.WebXRBackgroundRemover)

              // marker = BABYLON.MeshBuilder.CreateTorus('marker', { diameter: 0.15, thickness: 0.05 });
              // marker.isVisible = false;
              // marker.rotationQuaternion = new BABYLON.Quaternion();

              // arModelNode = model.clone(
              //     'arModelNode',
              //     null
              // )
              // model.setEnabled(false)
              break
          case WebXRState.IN_XR:
              console.log('IN_XR')
              this.setState({
                inARMode: true
              }, () => {
                this.props.isOnARMode(true)
              });
              break
          case WebXRState.EXITING_XR:
              console.log('EXITING_XR')
              break
          case WebXRState.NOT_IN_XR:
            if(this.globalMesh){
              this.globalMesh.rotation = new Vector3(0, Math.PI, 0);
              this.globalMesh.position = new Vector3(0, 0, 0);
            }
            if(this.globalCamera){
              this.globalCamera.wheelDeltaPercentage = 0.1
              this.globalCamera.minZ = 0.001;
              this.globalCamera.maxZ = 20
              this.globalCamera.lockedTarget = new Vector3(0, 1, 0)
              this.globalCamera.lowerRadiusLimit = 1.0
              this.globalCamera.upperRadiusLimit = 5.0

              this.globalCamera.alpha = Math.PI / 2
              this.globalCamera.beta = Math.PI / 2
              this.globalCamera.radius = 3
            }
            
            
            this.props.isOnARMode(false)
            this.setState({
              inARMode: false
            }, () => {
              this.props.isOnARMode(false)
            });
              // marker.dispose(undefined, true)
              // arModelNode.dispose(undefined, true)
              // model.setEnabled(true)
              console.log('NOT_IN_XR')
              break
      }
  })

    scene.onPointerObservable.add(() => {
        if(hitTest && marker.isVisible) {
            if(this.globalMesh && this.globalCamera){
              this.globalMesh.isVisible = true
              this.globalMesh.setEnabled(true);
              this.globalMesh.position.copyFrom(marker.position);
              this.globalMesh.lookAt(this.globalCamera.position)
              this.globalMesh.rotation = new Vector3(0, -this.globalMesh.rotation.y, 0);
              marker.isVisible = false;
              marker.setEnabled(false);
            }
            //const newBox = box.clone();
            //newBox.isVisible = true;
            //newBox.position.copyFrom(marker.position);

            // you can also take rotationQuaternion
        }
    }, PointerEventTypes.POINTERDOWN)
  }

  LoadAllTexturesAndModels = async (scene: Scene): Promise<void> => {

    console.log("Loading model")
    {
      const { meshes, animationGroups } = await SceneLoader.ImportMeshAsync('', './models/', 'Rem_glb.glb', scene)
      this.globalMesh = meshes[0] as Mesh;
      console.log(this.globalMesh)
      //this.globalMesh.rotation = new Vector3(90 * Math.PI / 180, 180 * Math.PI / 180, 0)
      
      for(const meshArmature of this.globalMesh.getChildren()){
        //(meshArmature as TransformNode).rotation = Vector3.Zero()
        for(const meshConfig of meshArmature.getChildren()){
          const partName = meshConfig.name.toLowerCase()
          const enablePart = partName.includes("body") || partName.includes("hips") || partName.includes("wigs_rem") || partName.includes("shoes_domina")
          meshConfig.setEnabled(enablePart)
        }
      }
      this.animationIdle = animationGroups[0]
      this.animationTalk = animationGroups[1]
    }

    const hdrTexture = CubeTexture.CreateFromPrefilteredData("./models/environmentSpecular.env", scene);
    scene.environmentTexture = hdrTexture;
    scene.environmentIntensity = 0.6
    
    this.props.sceneLoaded(true)
    this.setState({ sceneLoaded: true })
  }


  render(): React.ReactNode {
    return (
      <div className={`full-scene3d-container ${this.state.inARMode ? 'armode' : ''}`}>
        <canvas ref={this.canvasRef} style={{ width: '100%', height: '100%' }}></canvas>
      </div>
    )
  }
}

export default Scene3D